import { createContext, useState, useEffect, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { addViews, getViews } from "../../Api";
import SyncContext from "../../context/SyncContext";
import { IdeasContextType, initialState } from "../../types/types";
import FiltersContext from "./filters/context/FiltersContext";
import { getFilteredRequests as fetchFilteredRequests, parseFiltersFromQuery } from "./ideasHelpers";

const IdeasContext = createContext<IdeasContextType>(initialState);

const IdeasContextProvider = ({ children }: { children: React.ReactNode }) => {

  const { ideaColumns, ideaDefaultSortColumn, ideasFiltersConfig, impactAggregationProperties, subPages, fields } = useContext(SyncContext);
  const { activeFilters, subPageName, subPageKey } = useContext(FiltersContext);

  const location = useLocation();
  const auth0 = useAuth0();
  const queryParams = new URLSearchParams(location.search);
  const _page = queryParams.get("page");
  const _selectedView = queryParams.get("selectedView");
  const [ideas, setIdeas] = useState<any[]>([]);

  const [page, setPage] = useState<string | null>(_page || null);
  const [requestsTotalCount, setRequestsTotalCount] = useState(0);
  const [sortState, setSortState] = useState<string>("total");
  const [filters, setFilters] = useState<any>(() => {
    parseFiltersFromQuery(location.search, ideasFiltersConfig) ?? []
  });
  const [sortStatus, setSortStatus] = useState<any>({});
  const [isTagsHovered, setIsTagsHovered] = useState<boolean>(false);
  const [hoveredId, setHoveredId] = useState("");
  const [savedViews, setSavedViews] = useState<any[]>([]);
  const [selectedView, setSelectedView] = useState<any>();
  const [search, setSearch] = useState("");
  const [, setLastFilterParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [isArchived, setIsArchived] = useState(false);

  const getFilteredIdeas = () => {
   
    fetchFilteredRequests(
      sortStatus,
      setLastFilterParams,
      page,
      search,
      activeFilters,
      setLoading,
      setIdeas,
      setRequestsTotalCount, 
      setPage,
      ideasFiltersConfig,
      selectedView?.name,
      auth0,
      isArchived,
      location.pathname
    );
  };

  const loadViews = async (deleted = false) => {
    try {
      const { views } = await getViews(auth0);
      let filteredViews;
      const subPage = fields?.subPages?.find((page: any) => page.key === subPageKey);
      const enableBagelAI = subPage?.enableBagelAI;
      if(subPageKey){
        
        //update views if subpageKey not exist in view.subPageKeys
        const processedViews = await Promise.all(views.map(async (view: any) => {
          if (!view.subPageKey) {
            view.subPageKey = [];
          }
          if (view.isSubPageView && !view.subPageKeys.includes(subPageKey)) {
            view.subPageKeys.push(subPageKey);
            await addViews({
              _id: view._id,
              subPageKey: subPageKey,
              isGlobal: view.isGlobal,
              isSubPageView: view.isSubPageView
            }, auth0);
          }
          return view;
        }));
        filteredViews = processedViews.filter((view: any) => 
          (enableBagelAI && view.name == 'Bagel AI') ||
          view.subPageKeys?.includes(subPageKey)
        );
        //add  subpage's defaultFilter  in each views's filters array
        filteredViews.forEach((view: any) => {
          if (subPage?.defaultFilters && Array.isArray(subPage.defaultFilters)) {
            const defaultFiltersWithKey = subPage.defaultFilters.map((filter: any) => ({
              ...filter,
              default: true
            }));
            view.filters.unshift(...defaultFiltersWithKey);
          } else if (subPage?.defaultFilters) {
            const defaultFilterWithKey = {
              ...subPage.defaultFilters,
              default: true
            };
            view.filters.unshift(defaultFilterWithKey);
          }
        });
      } else {
        filteredViews = views.filter((view: any) => 
          view?.createdBy !== 'subpage'
        );
        //add defaultFilter  in each views's filters array
        filteredViews.forEach((view: any) => {
          if (fields?.defaultFilters && Array.isArray(fields?.defaultFilters)) {
            const defaultFiltersWithKey = fields.defaultFilters.map((filter: any) => ({
              ...filter,
              default: true
            }));
            view.filters.unshift(...defaultFiltersWithKey);
          } else if (fields?.defaultFilters) {
            const defaultFilterWithKey = {
              ...fields.defaultFilters,
              default: true
            };
            view.filters.unshift(defaultFilterWithKey);
          }
        });
      }
      setSavedViews(filteredViews);
      if (!_selectedView || deleted || filteredViews.length <= 1) {
        setSelectedView(filteredViews[0]);
      } else if(_selectedView){
        const selected = filteredViews.find((sv:any) => sv.name.toLowerCase() == _selectedView.toLowerCase())
        if(selected){
          setSelectedView(selected)
        }else{
          setSelectedView(filteredViews[0])
        }
      }
    } catch (error) {
      console.error("Failed to load views:", error);
    }
  };

  useEffect(() => {
    loadViews();
  }, [subPageKey, fields?.subPages, fields?.defaultFilters]);

  const upsertView = async (body: any, callback: (res: any) => void) => {
    try {
      const res = await addViews(body, auth0);
      callback(res);
      await loadViews(body?.deleted);
    } catch (error) {
      console.error("Failed to upsert view:", error);
    }
  };

  const hoverTags = (e: any, id: string) => {
    if (e.type === "mouseleave") {
      setIsTagsHovered(false);
      setHoveredId(id);
    } else if (e.type === "mouseenter") {
      setIsTagsHovered(true);
      setHoveredId(id);
    }
  };

  useEffect(() => {
    const _sortStatus = queryParams.get("sortStatus");
    let _sortStatusParams;

    try {
      _sortStatusParams = _sortStatus ? JSON.parse(_sortStatus) : null;
    } catch (error) {
      console.error("Invalid JSON in sortStatus:", error);
      _sortStatusParams = null;
    }


    if (_sortStatusParams?.columnAccessor && _sortStatusParams.columnAccessor !== "null" && _sortStatusParams.columnAccessor !== null) {
      setSortStatus(_sortStatusParams);
    } else if (ideaDefaultSortColumn) {
      setSortStatus({ columnAccessor: ideaDefaultSortColumn, direction: "asc" });
    }
  }, [impactAggregationProperties, ideaDefaultSortColumn, ideaColumns]);
  

  const tableColoumns = useMemo(() => {
    // If we have a subPageKey, use the subpage's columns directly
    if(subPageKey){
      const subPage = fields?.subPages?.find((page: any) => page.key === subPageKey);
      if (subPage && subPage.ideaColumns) {
        const subPageColumns = subPage.ideaColumns;
        
        if (selectedView?.name === 'Bagel AI') {
          const relevantColoumns = subPageColumns.filter((column: any) => column.generatedEnabled !== false)
          return relevantColoumns
        }
        
        return subPageColumns;
      }
    }

    // Use regular ideaColumns for non-subpage views
    const columns = ideaColumns;
    if (selectedView?.name === 'Bagel AI') {
      const relevantColoumns = columns.filter((column: any) => column.generatedEnabled !== false)
      return relevantColoumns
    }

    return columns;
  }, [ideaColumns, selectedView, subPages, subPageKey]);

  


  return (
    <IdeasContext.Provider
      value={{
        location,
        page,
        filters,
        sortStatus,
        savedViews,
        selectedView,
        requestsTotalCount,
        setRequestsTotalCount,
        setPage,
        setFilters,
        setSortStatus,
        setSelectedView,
        loadViews,
        upsertView,
        sortState,
        setSortState,
        isTagsHovered,
        hoverTags,
        hoveredId,
        search,
        setSearch,
        loading,
        setLoading,
        ideas,
        setIdeas,
        getFilteredIdeas,
        tableColoumns,
        isArchived,
        setIsArchived
      }}
    >
      {children}
    </IdeasContext.Provider>
  );
};

export { IdeasContext, IdeasContextProvider };
export default IdeasContext;
